import { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '@/App';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import JitsiCall from '@/components/JitsiCall';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Phone, Plus, Users, X, Search, UserPlus, PhoneOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GroupCalls() {
  const { user, token } = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomDesc, setNewRoomDesc] = useState('');
  const [loading, setLoading] = useState(true);

  // Invite modal state
  const [showInvite, setShowInvite] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [connectedUsers, setConnectedUsers] = useState([]);

  const fetchRooms = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(res.data);
    } catch (err) {
      console.error('Failed to fetch rooms');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchConnections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/connections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const connections = res.data;
      
      // Fetch user details for each connected user
      const users = [];
      for (const conn of connections) {
        const otherId = conn.user_id_1 === user.id ? conn.user_id_2 : conn.user_id_1;
        try {
          const userRes = await axios.get(`${API}/users/${otherId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          users.push({ id: otherId, name: userRes.data.name, photo: userRes.data.profile_photo });
        } catch (e) {}
      }
      setConnectedUsers(users);
    } catch (err) {
      console.error('Failed to fetch connections');
    }
  }, [token, user?.id]);

  useEffect(() => {
    fetchRooms();
    fetchConnections();
  }, [fetchRooms, fetchConnections]);

  // Auto-join room from URL param
  useEffect(() => {
    const roomId = searchParams.get('room');
    if (roomId && !activeRoom) {
      joinRoom(roomId);
    }
  }, [searchParams]);

  const handleCloseJitsi = useCallback(() => {
    setActiveRoom(null);
    fetchRooms();
  }, [fetchRooms]);

  const createRoom = async () => {
    if (!newRoomName.trim()) {
      toast.error('Please enter a room name');
      return;
    }
    try {
      const res = await axios.post(`${API}/rooms/create`, {
        name: newRoomName.trim(),
        description: newRoomDesc.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Room created!');
      const name = newRoomName.trim();
      setNewRoomName('');
      setNewRoomDesc('');
      setShowCreate(false);
      setActiveRoom({ id: res.data.room_id, jitsi_room: res.data.jitsi_room, name });
      fetchRooms();
    } catch (err) {
      toast.error('Failed to create room');
    }
  };

  const joinRoom = async (roomId) => {
    try {
      const res = await axios.get(`${API}/rooms/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.data.is_active) {
        toast.error('This room has been closed');
        return;
      }
      setActiveRoom(res.data);
    } catch (err) {
      toast.error('Room not found or expired');
    }
  };

  const endRoom = async (roomId) => {
    try {
      await axios.post(`${API}/rooms/${roomId}/end`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Room closed');
      if (activeRoom?.id === roomId) setActiveRoom(null);
      fetchRooms();
    } catch (err) {
      toast.error('Failed to close room');
    }
  };

  const inviteUsers = async (roomId, userIds) => {
    try {
      await axios.post(`${API}/rooms/${roomId}/invite`, {
        user_ids: userIds
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invitation sent!');
      setShowInvite(null);
    } catch (err) {
      toast.error('Failed to send invite');
    }
  };

  const filteredUsers = connectedUsers.filter(u => {
    if (!searchQuery) return true;
    return u.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Active Jitsi call
  if (activeRoom) {
    return (
      <JitsiCall
        roomName={activeRoom.jitsi_room}
        displayName={user?.name}
        userEmail={user?.email}
        userPhoto={user?.profile_photo}
        onClose={handleCloseJitsi}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 to-teal-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 pt-24 pb-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Group Calls</h1>
            <p className="text-gray-500 mt-1">Create or join group video calls with your connections</p>
          </div>
          <Button
            data-testid="create-room-btn"
            onClick={() => setShowCreate(true)}
            className="bg-coral hover:bg-coral/90 text-white rounded-full px-6"
          >
            <Plus className="w-4 h-4 mr-2" /> New Room
          </Button>
        </div>

        {/* Create Room Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" data-testid="create-room-modal">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Create Group Call</h2>
                <button onClick={() => setShowCreate(false)} data-testid="close-create-modal">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Room Name</label>
                  <Input
                    data-testid="room-name-input"
                    value={newRoomName}
                    onChange={(e) => setNewRoomName(e.target.value)}
                    placeholder="e.g., Weekend Hangout"
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Description (optional)</label>
                  <Input
                    data-testid="room-desc-input"
                    value={newRoomDesc}
                    onChange={(e) => setNewRoomDesc(e.target.value)}
                    placeholder="What's this call about?"
                    className="rounded-xl"
                  />
                </div>
                <Button
                  data-testid="confirm-create-room-btn"
                  onClick={createRoom}
                  className="w-full bg-coral hover:bg-coral/90 text-white rounded-xl"
                >
                  Create & Join
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Invite Modal */}
        {showInvite && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center" data-testid="invite-modal">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Invite to Call</h2>
                <button onClick={() => { setShowInvite(null); setSearchQuery(''); }} data-testid="close-invite-modal">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  data-testid="invite-search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search connections..."
                  className="pl-9 rounded-xl"
                />
              </div>
              <div className="overflow-y-auto flex-1 space-y-2">
                {filteredUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={u.photo} />
                        <AvatarFallback className="bg-teal text-white">{u.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{u.name}</span>
                    </div>
                    <Button
                      size="sm"
                      data-testid={`invite-user-${u.id}`}
                      onClick={() => inviteUsers(showInvite, [u.id])}
                      className="bg-teal hover:bg-teal/90 text-white rounded-full text-xs"
                    >
                      <UserPlus className="w-3 h-3 mr-1" /> Invite
                    </Button>
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <p className="text-center text-gray-400 py-8">
                    {connectedUsers.length === 0 ? 'Connect with people first to invite them' : 'No connections found'}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Rooms List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-coral border-t-transparent" />
          </div>
        ) : rooms.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-semibold text-gray-600 mb-2">No active rooms</h2>
            <p className="text-gray-400 mb-6">Create a room to start a group call with your connections</p>
            <Button
              data-testid="create-first-room-btn"
              onClick={() => setShowCreate(true)}
              className="bg-coral hover:bg-coral/90 text-white rounded-full px-8"
            >
              <Plus className="w-4 h-4 mr-2" /> Create First Room
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {rooms.map(room => (
              <div
                key={room.id}
                data-testid={`room-card-${room.id}`}
                className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-coral/10 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-coral" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{room.name}</h3>
                        {room.description && (
                          <p className="text-sm text-gray-500">{room.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        {room.participants?.length || 0} participant{room.participants?.length !== 1 ? 's' : ''}
                      </span>
                      <span>Created by {room.creator_name}</span>
                      <span>{new Date(room.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      data-testid={`invite-room-${room.id}`}
                      onClick={() => setShowInvite(room.id)}
                      className="bg-teal/10 text-teal hover:bg-teal/20 rounded-full"
                    >
                      <UserPlus className="w-4 h-4 mr-1" /> Invite
                    </Button>
                    <Button
                      data-testid={`join-room-${room.id}`}
                      size="sm"
                      onClick={() => joinRoom(room.id)}
                      className="bg-coral hover:bg-coral/90 text-white rounded-full"
                    >
                      <Phone className="w-4 h-4 mr-1" /> Join
                    </Button>
                    {room.creator_id === user?.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`end-room-${room.id}`}
                        onClick={() => endRoom(room.id)}
                        className="text-red-500 hover:bg-red-50 rounded-full"
                      >
                        <PhoneOff className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
